/* landingShared.jsx — shared tokens, data, glyphs and helpers for the JAI
   marketing landing page. Ported from the "JAI Onboarding" design prototype and
   wired to the app's real brand system (JaiBrand). */

import { useState, useEffect, useRef } from 'react'
import { JAI_COLORS, JAI_FONTS, JaiMark } from '../JaiBrand'
import { APP_STORE_URL } from '../../config/appStore'

/* The prototype's token object had two extra keys the landing uses (faint,
   hairSoft). Extend the real brand colors rather than duplicate them. */
export const JAI = {
  ...JAI_COLORS,
  faint: 'rgba(10,31,68,0.40)',
  hairSoft: 'rgba(10,31,68,0.07)',
}
export const FONTS = JAI_FONTS

/* ── The nine league tools. Single source of truth — headline copy reads its
   count from L_TOOL_COUNT / L_TOOL_COUNT_WORD below. ─────────────────────── */
export const L_TOOLS = [
  { id: 'waiver', name: 'Waiver Wire', desc: 'Top pickups ranked for your roster — before your rivals wake up.' },
  { id: 'trade', name: 'Trade Analyser', desc: 'Every offer graded in seconds, with a counter already drafted.' },
  { id: 'player', name: 'Player Analyser', desc: 'Deep dives on any player: form, role, matchup and risk.' },
  { id: 'team', name: 'Team Analyser', desc: 'Your strengths and gaps, measured against the whole league.' },
  { id: 'scout', name: 'Opponent Scout', desc: 'A game plan built for this week’s matchup.' },
  { id: 'draft', name: 'Draft Room', desc: 'Live draft help tuned to your pick, scoring and roster.' },
  { id: 'schedule', name: 'Schedule Surgeon', desc: 'Game volume per roster slot — spot the light weeks early.' },
  { id: 'injury', name: 'Injury Insurance', desc: 'JAI lines up the handcuff before you ever need it.' },
  { id: 'notes', name: 'My Notes', desc: 'Private strategy notes JAI reads for context.' },
]

export const L_TOOL_COUNT = L_TOOLS.length
const L_TOOL_NUM_WORDS = {
  1: 'One', 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five', 6: 'Six', 7: 'Seven',
  8: 'Eight', 9: 'Nine', 10: 'Ten', 11: 'Eleven', 12: 'Twelve',
}
export const L_TOOL_COUNT_WORD = L_TOOL_NUM_WORDS[L_TOOL_COUNT] || String(L_TOOL_COUNT)

export const HOW_STEPS = [
  {
    n: '01', t: 'Sync your league',
    d: 'Connect the platform you already play on — ESPN, Yahoo, Sleeper and more. JAI imports your rosters, scoring and schedule in about a minute. No setup, no spreadsheets.',
  },
  {
    n: '02', t: 'JAI goes to work',
    d: 'Ask JAI is where it all happens — the app’s headline feature. Ask anything, from a start/sit to a trade or a waiver claim, and it answers in seconds, drawing on every game, injury report and wire it watches across your leagues around the clock.',
  },
  {
    n: '03', t: 'Make the winning move',
    d: 'Clear, graded calls — who to start, who to claim, when to trade — each one with the reasoning and the receipts. You walk into every matchup already knowing the play, and exactly why it’s the right one.',
  },
]

export const ABOUT_PRINCIPLES = [
  { t: 'Always reasoned', d: 'Every call comes with the why and the receipts. No black boxes, no “trust me”.' },
  { t: 'Tuned to you', d: 'Your scoring, your roster, your rivals — never generic, one-size-fits-all advice.' },
  { t: 'Always on', d: 'While you sleep, JAI is scanning every game and wire. You just get the verdict.' },
]

/* ── Tool glyphs (stroke icons) ──────────────────────────────────────────── */
export function ToolGlyph({ id, size = 22, color = 'currentColor' }) {
  const s = { width: size, height: size, display: 'block' }
  const sw = 1.6
  switch (id) {
    case 'ask':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v7A2.5 2.5 0 0 1 17.5 15H10l-4.2 3.4V15H6.5A2.5 2.5 0 0 1 4 12.5v-7Z" stroke={color} strokeWidth={sw} strokeLinejoin="round"></path><path d="M12 6.6l0.85 2.05 2.05 0.85-2.05 0.85L12 12.4l-0.85-2.05L9.1 9.5l2.05-0.85L12 6.6Z" fill={color}></path></svg>)
    case 'waiver':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={color} strokeWidth={sw}></circle><path d="M12 8v8M8 12h8" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    case 'trade':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><path d="M4 8h13l-3-3M20 16H7l3 3" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"></path></svg>)
    case 'player':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3.4" stroke={color} strokeWidth={sw}></circle><path d="M5 20c1.2-3.6 3.8-5.2 7-5.2s5.8 1.6 7 5.2" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    case 'team':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><path d="M12 3l8 3v5c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6l8-3Z" stroke={color} strokeWidth={sw} strokeLinejoin="round"></path><path d="M8.5 12l2.4 2.4 4.6-4.8" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"></path></svg>)
    case 'scout':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.4" stroke={color} strokeWidth={sw}></circle><circle cx="12" cy="12" r="3" stroke={color} strokeWidth={sw}></circle><path d="M12 3.6V7M12 17v3.4M3.6 12H7M17 12h3.4" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    case 'schedule':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="15" rx="2" stroke={color} strokeWidth={sw}></rect><path d="M4 9.5h16M8 3v4M16 3v4" stroke={color} strokeWidth={sw} strokeLinecap="round"></path><path d="M8 13h2M14 13h2M8 16.5h2M14 16.5h2" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    case 'injury':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><path d="M12 3l8 3v5c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6l8-3Z" stroke={color} strokeWidth={sw} strokeLinejoin="round"></path><path d="M12 8.5v6M9 11.5h6" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    case 'draft':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><rect x="5" y="4" width="14" height="17" rx="2" stroke={color} strokeWidth={sw}></rect><path d="M9 3.5h6v2.5H9z" stroke={color} strokeWidth={sw} strokeLinejoin="round"></path><path d="M8.5 12l2 2 4-4.4" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"></path><path d="M8.5 17.5h7" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    case 'notes':
      return (<svg style={s} viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14a0 0 0 0 1 0 0H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke={color} strokeWidth={sw} strokeLinejoin="round"></path><path d="M14 3v5h5" stroke={color} strokeWidth={sw} strokeLinejoin="round"></path><path d="M8.5 13h7M8.5 16.5h5" stroke={color} strokeWidth={sw} strokeLinecap="round"></path></svg>)
    default:
      return null
  }
}

export function LArrow({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M2.5 8h10M9 3.5 13.5 8 9 12.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
    </svg>
  )
}

export function LCheck({ size = 13, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M2.8 8.6 6.2 12 13.2 4.4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
    </svg>
  )
}

/* ── App Store ─────────────────────────────────────────────────────────────
   The website is currently the only route to the iOS app: App Store search
   has not indexed a brand-new app, and a three-letter name would not out-rank
   ESPN or Sleeper once it does. So this button is the download path, not a
   decoration.

   The badge is drawn inline rather than loaded from Apple's badge CDN because
   the Content-Security-Policy in backend/src/index.js leaves img-src at
   helmet's default ('self' data:) — a remote badge would be blocked with no
   visible error. To use Apple's official artwork instead, drop the PNG in
   frontend/public/ and swap the <svg> below for an <img>. */

function AppleLogo({ size = 19, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true" style={{ flexShrink: 0, marginTop: -2 }}>
      <path d="M17.05 12.54c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.42-.14-2.76.83-3.48.83-.71 0-1.82-.81-2.99-.79-1.54.02-2.96.89-3.75 2.26-1.6 2.77-.41 6.88 1.15 9.13.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.78.74 2.99.72 1.23-.02 2.02-1.12 2.78-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.64zM14.8 5.36c.63-.77 1.06-1.83.94-2.9-.91.04-2.01.61-2.66 1.37-.59.68-1.1 1.76-.96 2.8 1.01.08 2.05-.52 2.68-1.27z"></path>
    </svg>
  )
}

/* label: the small line above ("Download on the"). Apple's own badge wording. */
export function AppStoreButton({ dark = false, style }) {
  const fg = dark ? 'white' : JAI.navy
  const bg = dark ? 'rgba(255,255,255,0.07)' : 'white'
  const edge = dark ? 'rgba(255,255,255,0.22)' : 'rgba(10,31,68,0.16)'
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="jai-appstore-btn"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 11,
        background: bg, color: fg, border: `1px solid ${edge}`,
        borderRadius: 13, padding: '11px 20px 12px', textDecoration: 'none',
        fontFamily: FONTS.tight, lineHeight: 1.05, whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <AppleLogo size={22} color={fg}></AppleLogo>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.02em', opacity: 0.72 }}>Download on the</span>
        <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>App Store</span>
      </span>
    </a>
  )
}

/* ── scroll-reveal wrapper — IntersectionObserver + rect-check + timeout
   fallback so content can never stay permanently hidden. ─────────────────── */
export function useInView(threshold = 0.18, timeoutMs = 1600) {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let done = false
    const mark = () => { if (!done) { done = true; setVis(true); cleanup() } }
    const check = () => {
      const r = el.getBoundingClientRect()
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) mark()
    }
    let io = null
    try {
      io = new IntersectionObserver(([e]) => { if (e.isIntersecting) mark() }, { threshold })
      io.observe(el)
    } catch (e) { /* fall through to scroll fallback */ }
    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check)
    const t1 = setTimeout(check, 350)
    const t2 = setTimeout(mark, timeoutMs) // safety: never permanently invisible
    function cleanup() {
      if (io) io.disconnect()
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
      clearTimeout(t1); clearTimeout(t2)
    }
    return cleanup
  }, [])
  return [ref, vis]
}

export function Reveal({ children, delay = 0, style, className = '', ...rest }) {
  const [ref, vis] = useInView()
  const [committed, setCommitted] = useState(false)
  useEffect(() => {
    if (!vis) return
    // hard-commit the final visible state once the transition should be done,
    // guarding against throttled/frozen CSS transitions (backgrounded tabs).
    const t = setTimeout(() => setCommitted(true), delay + 850)
    return () => clearTimeout(t)
  }, [vis])
  const st = committed
    ? { opacity: 1, transform: 'none', transition: 'none', ...style }
    : { transitionDelay: `${delay}ms`, ...style }
  return (
    <div ref={ref} className={'v2-reveal' + (vis ? ' in' : '') + ' ' + className} style={st} {...rest}>
      {children}
    </div>
  )
}

/* ── shared footer ───────────────────────────────────────────────────────── */
export function LFooter({ dark = false, onNav }) {
  const c = dark ? 'rgba(255,255,255,0.45)' : JAI.faint
  const link = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12.5, color: c, fontWeight: 600, cursor: 'pointer' }
  return (
    <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '28px 64px', borderTop: dark ? '1px solid rgba(255,255,255,0.08)' : `1px solid ${JAI.hairSoft}`, fontFamily: FONTS.tight, flexWrap: 'wrap', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <JaiMark size={24} bg={dark ? JAI.blue : JAI.navy} accent={JAI.blueBright}></JaiMark>
        <span style={{ fontSize: 13, color: c, fontWeight: 600 }}>© 2026 JAI</span>
      </div>
      <div style={{ display: 'flex', gap: 24 }}>
        <button type="button" style={link} onClick={() => onNav && onNav('terms')}>Terms</button>
        <button type="button" style={link} onClick={() => onNav && onNav('privacy')}>Privacy</button>
        <button type="button" style={link} onClick={() => onNav && onNav('contact')}>Contact</button>
      </div>
    </footer>
  )
}
